import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Show, SignIn, SignUp, useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api, setTokenGetter, errCode } from "@/lib/api";
import { MeContext } from "@/lib/me";
import AuthLayout from "@/pages/AuthLayout";
import AccessDenied from "@/pages/AccessDenied";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/components/app/AppShell";
import Dashboard from "@/pages/Dashboard";
import Radar from "@/pages/Radar";
import Editorial from "@/pages/Editorial";
import StoryWorkspace from "@/pages/StoryWorkspace";
import Flatplan from "@/pages/Flatplan";
import CRM from "@/pages/CRM";
import Integrations from "@/pages/Integrations";
import Team from "@/pages/Team";
import Agenda from "@/pages/Agenda";
import PublicQuote from "@/pages/PublicQuote";
import { Splash } from "@/components/app/shared";

function TokenBridge() {
  const { getToken } = useAuth();
  setTokenGetter(getToken);
  return null;
}

function MemberGate() {
  const { data: me, isLoading, error, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get("/me")).data,
    retry: (count, e) => e?.response?.status !== 403 && count < 2,
    staleTime: 5 * 60_000,
  });
  if (isLoading) return <Splash label="Verificando acceso" />;
  if (error && errCode(error) === "domain_denied") return <AccessDenied email={error.response.data.detail.email} />;
  if (error) return <Splash label="No pudimos cargar tu perfil" action={() => refetch()} />;
  if (!me.onboarded) return <Onboarding me={me} onDone={refetch} />;
  return (
    <MeContext.Provider value={me}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Dashboard />} />
          <Route path="radar" element={<Radar />} />
          <Route path="editorial" element={<Editorial />} />
          <Route path="editorial/:id" element={<StoryWorkspace />} />
          <Route path="flatplan" element={<Flatplan />} />
          <Route path="crm" element={<CRM />} />
          <Route path="agenda" element={<Agenda />} />
          <Route path="integraciones" element={<Integrations />} />
          <Route path="equipo" element={<Team />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </MeContext.Provider>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="mediana-theme">
        <TooltipProvider delayDuration={200}>
          <BrowserRouter>
            <ClerkAuthProvider>
              <TokenBridge />
              <Routes>
                <Route path="/q/:token" element={<PublicQuote />} />
                <Route
                  path="/sign-in/*"
                  element={
                    <AuthLayout>
                      <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp />
                    </AuthLayout>
                  }
                />
                <Route
                  path="/sign-up/*"
                  element={
                    <AuthLayout>
                      <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />
                    </AuthLayout>
                  }
                />
                <Route
                  path="/*"
                  element={
                    <>
                      <Show when="signed-in">
                        <MemberGate />
                      </Show>
                      <Show when="signed-out">
                        <Navigate to="/sign-in" replace />
                      </Show>
                    </>
                  }
                />
              </Routes>
            </ClerkAuthProvider>
          </BrowserRouter>
          <Toaster position="bottom-right" />
        </TooltipProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
